import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";
import { api } from "@/lib/api";

const BRAND_CACHE_KEY = "ud_branding";
function readBrandingCache() {
  try { const raw = localStorage.getItem(BRAND_CACHE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function writeBrandingCache(b) {
  try { localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(b)); } catch {}
}

export const DEFAULT_BRANDING = {
  university_name: "University of Dallas",
  short_name: "Crusaders · Alumni",
  tagline: "Crusaders · Alumni Registry",
  login_headline: "Seek Wisdom. Live Abundantly.",
  login_subtext: "The official alumni registry of the University of Dallas — reconnect, steward, and celebrate our community of Crusaders.",
  logo_primary: "https://customer-assets-wrfwihn1.emergentagent.net/job_alumni-hub-101/artifacts/n52mfin2_image.png",
  logo_secondary: "/tower-braniff.png",
  university_url: "https://udallas.edu/",
  privacy_url: "",
  privacy_notice: "",
  login_bg: "",
  hero_bg: "",
  primary_color: "#001A4B",
  accent_color: "#C8A24C",
};

// Convert #RRGGBB -> "H S% L%" triplet for CSS custom properties consumed via hsl(var(--x)).
export function hexToHslTriplet(hex) {
  if (!hex) return null;
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyBrandingTheme(b) {
  const root = document.documentElement;
  const p = hexToHslTriplet(b.primary_color);
  const a = hexToHslTriplet(b.accent_color);
  if (p) root.style.setProperty("--primary", p);
  if (a) root.style.setProperty("--accent", a);
  if (b.university_name) document.title = `${b.university_name} — Alumni`;
}

const BrandingContext = createContext(DEFAULT_BRANDING);
export const useBranding = () => useContext(BrandingContext);

export function BrandingWatermark() {
  const b = useBranding();
  if (!b._resolved || !b.logo_secondary) return null;
  return <img src={b.logo_secondary} alt="" aria-hidden="true" className="pointer-events-none select-none fixed left-1/2 bottom-6 -translate-x-1/2 h-[calc(100vh-9rem)] w-auto object-contain opacity-[0.07] z-0" />;
}

export function BrandingProvider({ children }) {
  const cached = readBrandingCache();
  const [branding, setBranding] = useState(() => cached || DEFAULT_BRANDING);
  // Only trust image assets once resolved from cache (last known server state) or a fresh fetch,
  // so fresh loads with no cache don't flash the hardcoded default tower before branding loads.
  const [resolved, setResolved] = useState(() => !!cached);
  // Apply the cached/default theme synchronously on mount so a refresh keeps the color scheme.
  useLayoutEffect(() => { applyBrandingTheme(branding); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () =>
    api.get("/branding").then((r) => { const b = { ...DEFAULT_BRANDING, ...r.data }; setBranding(b); applyBrandingTheme(b); writeBrandingCache(b); setResolved(true); }).catch(() => {});
  useEffect(() => { refresh(); }, []);
  return <BrandingContext.Provider value={{ ...branding, _resolved: resolved, refresh, setBranding }}>{children}</BrandingContext.Provider>;
}
